/**
 * Scene-item layout maths, shared by the compositor (drawing) and the editor
 * (selection box, hit testing, drag handles) so both agree pixel for pixel.
 *
 * Order of operations for an item, from content pixels to parent pixels:
 *   crop -> scale (or fit into bounds) -> shift by alignment anchor -> rotate -> translate.
 */
import { Align, type Alignment, type BoundsType, type ItemTransform, type SceneItem, type Vec2 } from './model';

/** 2D affine matrix: x' = a*x + c*y + tx, y' = b*x + d*y + ty (canvas / Pixi layout). */
export interface Mat2D {
  a: number;
  b: number;
  c: number;
  d: number;
  tx: number;
  ty: number;
}

export const IDENTITY: Readonly<Mat2D> = Object.freeze({ a: 1, b: 0, c: 0, d: 1, tx: 0, ty: 0 });

/** Returns m1 * m2: applies m2 first, then m1. */
export function multiply(m1: Mat2D, m2: Mat2D): Mat2D {
  return {
    a: m1.a * m2.a + m1.c * m2.b,
    b: m1.b * m2.a + m1.d * m2.b,
    c: m1.a * m2.c + m1.c * m2.d,
    d: m1.b * m2.c + m1.d * m2.d,
    tx: m1.a * m2.tx + m1.c * m2.ty + m1.tx,
    ty: m1.b * m2.tx + m1.d * m2.ty + m1.ty,
  };
}

/** Returns null for a singular matrix (zero scale). */
export function invert(m: Mat2D): Mat2D | null {
  const det = m.a * m.d - m.b * m.c;
  if (Math.abs(det) < 1e-12) return null;
  const a = m.d / det;
  const b = -m.b / det;
  const c = -m.c / det;
  const d = m.a / det;
  return { a, b, c, d, tx: -(a * m.tx + c * m.ty), ty: -(b * m.tx + d * m.ty) };
}

export function applyToPoint(m: Mat2D, p: Vec2): Vec2 {
  return { x: m.a * p.x + m.c * p.y + m.tx, y: m.b * p.x + m.d * p.y + m.ty };
}

/** Offset of the alignment anchor inside a box of the given size. */
export function alignmentOffset(alignment: Alignment, width: number, height: number): Vec2 {
  let x = width / 2;
  if (alignment & Align.LEFT) x = 0;
  else if (alignment & Align.RIGHT) x = width;
  let y = height / 2;
  if (alignment & Align.TOP) y = 0;
  else if (alignment & Align.BOTTOM) y = height;
  return { x, y };
}

export interface ItemLayout {
  /** Size of the content after cropping, in content pixels. Zero if cropped away entirely. */
  contentSize: Vec2;
  /** Effective scale applied to the cropped content (derived from bounds when they are in use). */
  scale: Vec2;
  /** Maps cropped-content pixels (0..contentSize) to parent pixels. Use for drawing. */
  matrix: Mat2D;
  /** Size of the item box in parent pixels before rotation: the bounds, or the scaled content. May be negative when flipped without bounds. */
  boxSize: Vec2;
  /** Maps the unit square (0..1) to the item box in parent pixels. Use for selection outline and handles. */
  boxMatrix: Mat2D;
  /** Box corners in parent pixels: top-left, top-right, bottom-right, bottom-left (pre-flip naming). */
  corners: [Vec2, Vec2, Vec2, Vec2];
}

function placement(transform: ItemTransform, origin: Vec2): Mat2D {
  const rad = (transform.rotation * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  // T(position) * R(rotation) * T(-origin)
  return {
    a: cos,
    b: sin,
    c: -sin,
    d: cos,
    tx: transform.position.x - (cos * origin.x - sin * origin.y),
    ty: transform.position.y - (sin * origin.x + cos * origin.y),
  };
}

/**
 * Compute where an item's content lands in its parent.
 * @param sourceSize natural (uncropped) size of the content in pixels.
 */
export function computeItemLayout(transform: ItemTransform, sourceSize: Vec2): ItemLayout {
  const { crop } = transform;
  const width = Math.max(0, sourceSize.x - crop.left - crop.right);
  const height = Math.max(0, sourceSize.y - crop.top - crop.bottom);

  let scale: Vec2 = { x: transform.scale.x, y: transform.scale.y };
  let boxSize: Vec2;
  // Offset of the content's top-left inside the box, in parent pixels before rotation.
  let inset: Vec2 = { x: 0, y: 0 };

  if (transform.boundsType === 'none' || width === 0 || height === 0) {
    boxSize = { x: width * scale.x, y: height * scale.y };
  } else {
    const bounds = transform.bounds;
    // Fitting works on the content as already scaled by the item, so a
    // non-uniform item scale and its flip signs survive the fit.
    const baseW = width * Math.abs(scale.x);
    const baseH = height * Math.abs(scale.y);
    let type: BoundsType = transform.boundsType;
    if (type === 'max-only') {
      type = baseW > bounds.x || baseH > bounds.y ? 'scale-inner' : 'none';
    }
    if (baseW === 0 || baseH === 0) {
      type = 'none';
    } else if (type === 'scale-inner' || type === 'scale-outer') {
      let useWidth = bounds.x / bounds.y < baseW / baseH;
      if (type === 'scale-outer') useWidth = !useWidth;
      const mul = useWidth ? bounds.x / baseW : bounds.y / baseH;
      scale = { x: scale.x * mul, y: scale.y * mul };
    } else if (type === 'scale-to-width') {
      const mul = bounds.x / baseW;
      scale = { x: scale.x * mul, y: scale.y * mul };
    } else if (type === 'scale-to-height') {
      const mul = bounds.y / baseH;
      scale = { x: scale.x * mul, y: scale.y * mul };
    } else if (type === 'stretch') {
      scale = { x: Math.sign(scale.x || 1) * (bounds.x / width), y: Math.sign(scale.y || 1) * (bounds.y / height) };
    }
    boxSize = { x: bounds.x, y: bounds.y };
    const fittedW = width * Math.abs(scale.x);
    const fittedH = height * Math.abs(scale.y);
    inset = alignmentOffset(transform.boundsAlignment, bounds.x - fittedW, bounds.y - fittedH);
    // Flipped content is drawn towards negative coordinates; shift it back into its slot.
    if (scale.x < 0) inset.x += fittedW;
    if (scale.y < 0) inset.y += fittedH;
  }

  const anchor = alignmentOffset(transform.alignment, boxSize.x, boxSize.y);
  const place = placement(transform, anchor);
  const matrix = multiply(place, { a: scale.x, b: 0, c: 0, d: scale.y, tx: inset.x, ty: inset.y });
  const boxMatrix = multiply(place, { a: boxSize.x, b: 0, c: 0, d: boxSize.y, tx: 0, ty: 0 });
  const corners: ItemLayout['corners'] = [
    applyToPoint(boxMatrix, { x: 0, y: 0 }),
    applyToPoint(boxMatrix, { x: 1, y: 0 }),
    applyToPoint(boxMatrix, { x: 1, y: 1 }),
    applyToPoint(boxMatrix, { x: 0, y: 1 }),
  ];

  return { contentSize: { x: width, y: height }, scale, matrix, boxSize, boxMatrix, corners };
}

/** True if `point` (parent pixels) lies inside the item box. */
export function hitTestLayout(layout: ItemLayout, point: Vec2): boolean {
  const inverse = invert(layout.boxMatrix);
  if (!inverse) return false;
  const local = applyToPoint(inverse, point);
  return local.x >= 0 && local.x <= 1 && local.y >= 0 && local.y <= 1;
}

/**
 * Bounding box of a group's children in group-local pixels: the extent of
 * every child's laid-out corners, including negative coordinates. An empty
 * group is the zero box at the origin. Render the group's content offset by
 * `-min` and hit-test against the same box so drawing and editing agree.
 */
export function computeGroupBox(children: readonly SceneItem[], sizeOf: (item: SceneItem) => Vec2): { min: Vec2; max: Vec2 } {
  if (children.length === 0) return { min: { x: 0, y: 0 }, max: { x: 0, y: 0 } };
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const child of children) {
    const layout = computeItemLayout(child.transform, sizeOf(child));
    for (const corner of layout.corners) {
      if (corner.x < minX) minX = corner.x;
      if (corner.y < minY) minY = corner.y;
      if (corner.x > maxX) maxX = corner.x;
      if (corner.y > maxY) maxY = corner.y;
    }
  }
  return { min: { x: minX, y: minY }, max: { x: maxX, y: maxY } };
}

export interface GroupFrame {
  /** Top-left of the group's drawn extent in group-local px; never positive (the local origin is always inside). */
  min: Vec2;
  /** Extent size; the natural size handed to computeItemLayout for a group item. */
  size: Vec2;
}

export interface ItemExtent {
  size: Vec2;
  /** Group items only: that group's own `GroupFrame.min` (its displayed box is displaced by the origin shift). */
  min?: Vec2;
}

/**
 * Parent-space translation that keeps a group's local origin at `position` when the
 * group is left/top aligned with positive scale, or right/bottom aligned with negative
 * scale (the state Flip produces), and has no bounds. Zero otherwise (centre, bounds).
 */
export function groupOriginShift(transform: ItemTransform, frameMin: Vec2): Vec2 {
  if (transform.boundsType !== 'none') return { x: 0, y: 0 };
  const { alignment, scale, rotation } = transform;
  const horizontal = alignment & Align.LEFT ? scale.x > 0 : alignment & Align.RIGHT ? scale.x < 0 : false;
  const vertical = alignment & Align.TOP ? scale.y > 0 : alignment & Align.BOTTOM ? scale.y < 0 : false;
  const x = horizontal ? Math.abs(scale.x) * frameMin.x : 0;
  const y = vertical ? Math.abs(scale.y) * frameMin.y : 0;
  const rad = (rotation * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  return { x: cos * x - sin * y, y: sin * x + cos * y };
}

/**
 * Extent of the children's laid-out boxes (group-local px), unioned with the origin, so
 * children at negative coordinates render and legacy projects lay out identically.
 */
export function computeGroupFrame(children: readonly SceneItem[], measure: (item: SceneItem) => ItemExtent): GroupFrame {
  let minX = 0;
  let minY = 0;
  let maxX = 0;
  let maxY = 0;
  for (const child of children) {
    const extent = measure(child);
    const layout = computeItemLayout(child.transform, extent.size);
    const shift = extent.min ? groupOriginShift(child.transform, extent.min) : { x: 0, y: 0 };
    for (const corner of layout.corners) {
      const x = corner.x + shift.x;
      const y = corner.y + shift.y;
      if (x < minX) minX = x;
      if (y < minY) minY = y;
      if (x > maxX) maxX = x;
      if (y > maxY) maxY = y;
    }
  }
  return { min: { x: minX, y: minY }, size: { x: maxX - minX, y: maxY - minY } };
}

/** Maps group-local px to the group's parent space; children's parentMatrix = multiply(parentMatrix, this). */
export function groupContentMatrix(transform: ItemTransform, frame: GroupFrame): Mat2D {
  const layout = computeItemLayout(transform, frame.size);
  const shift = groupOriginShift(transform, frame.min);
  const { crop } = transform;
  const offset: Mat2D = { a: 1, b: 0, c: 0, d: 1, tx: -(crop.left + frame.min.x), ty: -(crop.top + frame.min.y) };
  return multiply({ a: 1, b: 0, c: 0, d: 1, tx: shift.x, ty: shift.y }, multiply(layout.matrix, offset));
}

/**
 * Natural size of a group measured from the group's local origin: children at
 * negative coordinates fall outside it.
 * @deprecated Use computeGroupBox; kept until the compositor and editor render with the `-min` offset.
 */
export function computeGroupSize(children: readonly SceneItem[], sizeOf: (item: SceneItem) => Vec2): Vec2 {
  const { max } = computeGroupBox(children, sizeOf);
  return { x: Math.max(0, max.x), y: Math.max(0, max.y) };
}

export function defaultTransform(): ItemTransform {
  return {
    position: { x: 0, y: 0 },
    scale: { x: 1, y: 1 },
    rotation: 0,
    alignment: Align.TOP | Align.LEFT,
    boundsType: 'none',
    boundsAlignment: Align.CENTER,
    bounds: { x: 0, y: 0 },
    crop: { left: 0, top: 0, right: 0, bottom: 0 },
  };
}
